import {
  isEditorialArchival,
  isVideoTemplate,
  isCreativeOnly,
  isEditorialOnly,
  isFilm,
  isImage,
  isOfflineContentOnly,
  SortEnum,
  type SearchParameters,
} from '../searchParameters';
import { SearchModeEnum, SearchModeFactory } from '../searchMode';
import type { SearchPreferences } from '../searchPreferences';

const searchModeFactory = new SearchModeFactory();

/**
 * The part of the sort decision the search itself cannot answer: whether the
 * visitor is a bot or first-time visitor, which takes the cheaper default.
 * Best match for a video-template search is derived from the search itself.
 */
export interface SortProfile {
  botOrFirstTimeVisitor: boolean;
}

const CREATIVE_IMAGE_SORTS = [
  SortEnum.MOST_POPULAR,
  SortEnum.BEST,
  SortEnum.NEWEST,
];
const ARCHIVAL_SORTS = [
  SortEnum.NEWEST,
  SortEnum.OLDEST,
  SortEnum.MOST_POPULAR,
];
const EVENT_SORTS = [
  SortEnum.BEST,
  SortEnum.NEWEST,
  SortEnum.OLDEST,
  SortEnum.MOST_POPULAR,
];
const VIDEO_TEMPLATE_SORTS = [SortEnum.BEST];

const isEvents = (searchParameters: SearchParameters): boolean =>
  !!searchParameters.events;

const isPortfolio = (searchParameters: SearchParameters): boolean =>
  searchModeFactory.fromSearchParameters(searchParameters).kind ===
  SearchModeEnum.CONTRIBUTOR;

const isAnalogOnly = (searchParameters: SearchParameters): boolean =>
  isOfflineContentOnly(searchParameters) && isFilm(searchParameters);

const isCreativeImage = (searchParameters: SearchParameters): boolean =>
  isImage(searchParameters) && isCreativeOnly(searchParameters);

/**
 * Whether a search offers a sort at all.
 *
 * The order of the branches carries meaning: an archival event search sorts by
 * the archival list, not the event one.
 *
 * Exported because which sorts a search offers changes with more than its
 * preference scope — archival, events, a portfolio and video templates each
 * narrow or widen the list — so a transition that stays in scope still has to
 * ask whether the sort it carries is one the new search offers.
 */
export const offersSort = (
  searchParameters: SearchParameters,
  sort: SortEnum
): boolean => {
  if (isEditorialArchival(searchParameters))
    return ARCHIVAL_SORTS.includes(sort);
  if (isEvents(searchParameters)) return EVENT_SORTS.includes(sort);
  if (isVideoTemplate(searchParameters))
    return VIDEO_TEMPLATE_SORTS.includes(sort);

  if (isCreativeImage(searchParameters)) {
    // A portfolio takes any sort, even inside the creative-image context that
    // otherwise narrows them.
    if (isPortfolio(searchParameters)) return true;
    return CREATIVE_IMAGE_SORTS.includes(sort);
  }

  return true;
};

/**
 * The sort the visitor last chose for this kind of search, or `undefined` when
 * the search is not one the visitor's sorts are remembered for.
 *
 * Only editorial searches remember a sort.
 */
const rememberedSort = (
  searchParameters: SearchParameters,
  preferences: SearchPreferences
): SortEnum | undefined => {
  if (isEvents(searchParameters)) return preferences.editorialEventSort;
  if (isEditorialArchival(searchParameters))
    return preferences.editorialArchivalSort;
  if (isEditorialOnly(searchParameters)) return preferences.editorialSort;

  return undefined;
};

/**
 * The cheaper sort a bot or first-time visitor gets. Best match is expensive
 * for the search back end, so it is the last resort rather than the first.
 */
const defaultSortForBotsAndNewVisitors = (
  searchParameters: SearchParameters
): SortEnum => {
  if (isVideoTemplate(searchParameters)) return SortEnum.BEST;
  if (offersSort(searchParameters, SortEnum.MOST_POPULAR))
    return SortEnum.MOST_POPULAR;
  if (offersSort(searchParameters, SortEnum.NEWEST)) return SortEnum.NEWEST;

  return SortEnum.BEST;
};

/** The sort a search falls back to when nothing is stated or remembered. */
const defaultSort = (
  searchParameters: SearchParameters,
  profile: SortProfile
): SortEnum => {
  if (profile.botOrFirstTimeVisitor)
    return defaultSortForBotsAndNewVisitors(searchParameters);
  if (isEditorialArchival(searchParameters) && !isEvents(searchParameters))
    return SortEnum.MOST_POPULAR;
  if (isAnalogOnly(searchParameters)) return SortEnum.NEWEST;

  return SortEnum.BEST;
};

/**
 * States the sort a search runs under, from what the search asked for, what the
 * visitor last chose, and what the search defaults to — in that order.
 *
 * Unlike the other rules here, this one always states a value: the sort a
 * search runs under is never genuinely absent — a request with no sort still
 * sorts.
 *
 * A stated sort the context does not offer falls through rather than being
 * cleared. That is deliberate: it is the same shape as the agreement
 * substitution in `reconcileAgreements` — the visitor asked for an ordering
 * that does not exist here, so they get the nearest one that does, not none.
 *
 * Known gap: the search service also recognises `purerecency` for
 * creative-image searches.
 * `SortEnum` has no such member, so a URL stating it resolves as unrecognised
 * before this rule sees it. Closing that needs the enum widened first.
 */
export const resolveSort = (
  searchParameters: SearchParameters,
  preferences: SearchPreferences,
  profile: SortProfile
): SearchParameters => {
  // A sort the search named. The getter answers `best` for a search that states
  // nothing, which is the right value to run under and the wrong answer to
  // whether the visitor chose it — so statedness is asked directly, and the
  // value only read once it has.
  const stated = searchParameters.states('sort')
    ? searchParameters.sort
    : undefined;
  const remembered = rememberedSort(searchParameters, preferences);

  let sort: SortEnum;
  if (stated && offersSort(searchParameters, stated)) {
    sort = stated;
  } else if (remembered && offersSort(searchParameters, remembered)) {
    sort = remembered;
  } else {
    sort = defaultSort(searchParameters, profile);
  }

  return sort === stated
    ? searchParameters
    : searchParameters.set('sort', sort);
};
